import { Button, Input } from '@artosera/ui';
import { ShieldAlert, ShieldCheck } from 'lucide-react';
import QRCode from 'qrcode';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authApi } from '../../api/auth.js';
import { estApiError, estErreurReseau } from '../../api/errors.js';
import { Field } from '../Field.js';
import styles from './ReglagesSecuritePage.module.css';

type Etape = 'repos' | 'scan' | 'codes' | 'desactivation';

export interface CarteDoubleAuthentificationProps {
  /** Le compte a-t-il déjà un second facteur actif ? */
  readonly actif: boolean;
  /** Après l'enrôlement (codes de secours notés) ou la désactivation : relire la session. */
  readonly apresChangement: () => Promise<void>;
}

/**
 * La double authentification d'un compte : son état, l'enrôlement réel contre
 * l'API, la désactivation. `POST /v1/auth/2fa/enroll` rend une URI
 * `otpauth://` — aucune image de QR n'est produite côté API
 * (`docs/dev/api.md` § 3) : le QR est dessiné ici, côté client, avec `qrcode`.
 *
 * Sans dépendance à la coquille : Réglages › Sécurité la pose dans une
 * galerie, `/app/double-authentification` hors de toute galerie.
 */
export function CarteDoubleAuthentification({
  actif,
  apresChangement,
}: CarteDoubleAuthentificationProps): JSX.Element {
  const { t } = useTranslation('regie');

  const [etape, setEtape] = useState<Etape>('repos');
  const [secret, setSecret] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [codesSecours, setCodesSecours] = useState<readonly string[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  function traiterErreur(error: unknown): void {
    if (estApiError(error)) {
      setErreur(error.message);
    } else if (estErreurReseau(error)) {
      setErreur(t('auth.connexion.erreurReseau'));
    } else {
      setErreur(t('commun.erreurReseau'));
    }
  }

  async function demarrerEnrolement(): Promise<void> {
    setErreur(null);
    setEnCours(true);
    try {
      const reponse = await authApi.mfaEnrolement();
      // L’URI porte le secret : usage local pour le QR, jamais en état ni en attribut.
      setQrDataUrl(await QRCode.toDataURL(reponse.otpauth));
      setSecret(reponse.secret);
      setEtape('scan');
    } catch (error) {
      traiterErreur(error);
    } finally {
      setEnCours(false);
    }
  }

  async function confirmer(): Promise<void> {
    setErreur(null);
    setEnCours(true);
    try {
      const reponse = await authApi.mfaConfirmer(code);
      setSecret(null);
      setQrDataUrl(null);
      setCodesSecours(reponse.codesSecours);
      setEtape('codes');
      setCode('');
    } catch (error) {
      traiterErreur(error);
    } finally {
      setEnCours(false);
    }
  }

  function abandonnerEnrolement(): void {
    setEtape('repos');
    setSecret(null);
    setQrDataUrl(null);
    setCode('');
  }

  async function terminer(): Promise<void> {
    abandonnerEnrolement();
    setCodesSecours([]);
    await apresChangement();
  }

  async function desactiver(): Promise<void> {
    setErreur(null);
    setEnCours(true);
    try {
      await authApi.mfaDesactiver({ code });
      setCode('');
      setEtape('repos');
      await apresChangement();
    } catch (error) {
      traiterErreur(error);
    } finally {
      setEnCours(false);
    }
  }

  return (
    <div className={styles.carte}>
      <div className={styles.statutLigne} data-actif={actif ? 'true' : 'false'}>
        <span className={styles.statutMarque} aria-hidden="true">
          {actif ? (
            <ShieldCheck size={22} strokeWidth={1.5} />
          ) : (
            <ShieldAlert size={22} strokeWidth={1.5} />
          )}
        </span>
        <p className={styles.statut}>
          {actif ? t('reglages.securite.statutActif') : t('reglages.securite.statutInactif')}
        </p>
      </div>

      {erreur ? (
        <p className={styles.erreur} role="alert">
          {erreur}
        </p>
      ) : null}

      {etape === 'repos' && !actif ? (
        <Button loading={enCours} onClick={() => void demarrerEnrolement()}>
          {t('reglages.securite.demarrer')}
        </Button>
      ) : null}

      {etape === 'repos' && actif ? (
        <Button variant="danger" onClick={() => setEtape('desactivation')}>
          {t('reglages.securite.boutonDesactiver')}
        </Button>
      ) : null}

      {etape === 'scan' && qrDataUrl ? (
        <div className={styles.etapeScan}>
          <div className={styles.etape}>
            <h2 className={styles.sousTitreEtape}>{t('reglages.securite.etape1Titre')}</h2>
            <p className={styles.description}>{t('reglages.securite.etape1Description')}</p>
            {qrDataUrl ? (
              <span className={styles.qrCadre}>
                <img
                  className={styles.qr}
                  src={qrDataUrl}
                  alt={t('reglages.securite.qrAlt')}
                  width={200}
                  height={200}
                />
              </span>
            ) : null}
            {secret ? (
              <p className={styles.secretManuel}>
                <span>{t('reglages.securite.secretManuel')}</span>
                <code>{secret}</code>
              </p>
            ) : null}
          </div>

          <div className={styles.etape}>
            <h2 className={styles.sousTitreEtape}>{t('reglages.securite.etape2Titre')}</h2>
            <Field label={t('reglages.securite.champCode')} htmlFor="mfa-code-confirmation">
              <Input
                id="mfa-code-confirmation"
                inputMode="numeric"
                maxLength={6}
                pattern="[0-9]*"
                autoComplete="one-time-code"
                mono
                value={code}
                onChange={(event) => setCode(event.target.value)}
              />
            </Field>
            <div className={styles.actions}>
              <Button loading={enCours} onClick={() => void confirmer()}>
                {t('reglages.securite.boutonConfirmer')}
              </Button>
              <Button variant="ghost" onClick={abandonnerEnrolement}>
                {t('reglages.securite.annuler')}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {etape === 'codes' ? (
        <div className={styles.codes}>
          <h2 className={styles.sousTitreEtape}>{t('reglages.securite.codesSecoursTitre')}</h2>
          <p className={styles.description}>{t('reglages.securite.codesSecoursDescription')}</p>
          <ul className={styles.listeCodes}>
            {codesSecours.map((valeur) => (
              <li key={valeur}>{valeur}</li>
            ))}
          </ul>
          <Button onClick={() => void terminer()}>
            {t('reglages.securite.codesSecoursTermine')}
          </Button>
        </div>
      ) : null}

      {etape === 'desactivation' ? (
        <div className={styles.desactivation}>
          <Field
            label={t('reglages.securite.champCodeDesactivation')}
            htmlFor="mfa-code-desactivation"
          >
            <Input
              id="mfa-code-desactivation"
              inputMode="numeric"
              maxLength={6}
              pattern="[0-9]*"
              autoComplete="one-time-code"
              mono
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
          </Field>
          <div className={styles.actions}>
            <Button variant="danger" loading={enCours} onClick={() => void desactiver()}>
              {t('reglages.securite.boutonDesactiver')}
            </Button>
            <Button variant="ghost" onClick={abandonnerEnrolement}>
              {t('reglages.securite.annuler')}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
